import {
  Copyright,
  OtpCodeField,
  LanguagePicker,
  isValidOtpCode,
  LoadingBackdrop,
  SnowSettingsButton,
  ColorModeIconDropdown,
} from 'components';
import {
  Box,
  Paper,
  Stack,
  Avatar,
  Button,
  Divider,
  TextField,
  Typography,
  IconButton,
  Grid2 as Grid,
  InputAdornment,
  Link as MuiLink,
} from '@mui/material';
import * as yup from 'yup';
import { STORAGE_KEY_ENUM } from 'enums';
import apiService from 'library/api-service';
import { Authentication, LocalStorage } from 'utils';
import { Link, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import emailImgSrc from 'assets/images/email-img.png';
import { FormattedMessage, useIntl } from 'react-intl';
import { PUBLIC_ROUTE_REGISTRY } from 'constants/routes';
import logoSrc from 'assets/images/idecon-header-logo.png';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { FormEvent, MouseEvent, useCallback, useRef, useState } from 'react';

export type LoginFormParams = {
  email: string;
  password: string;
};

const Login = () => {
  // other hooks
  const intl = useIntl();
  const navigate = useNavigate();
  const codeInputRef = useRef<HTMLInputElement>(null);
  const onClickLogoHandler = useCallback(() => navigate(PUBLIC_ROUTE_REGISTRY.login.path), [navigate]);

  // useState hooks
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showTwoFactorAuth, setShowTwoFactorAuth] = useState(false);

  // form validations & form state
  const schema = yup.object().shape({
    email: yup
      .string()
      .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' }))
      .email(intl.formatMessage({ id: 'login.invalidEmail', defaultMessage: 'Geçersiz e-posta formatı' }))
      .matches(
        /^[a-zA-Z0-9._%+-]+@(idecon\.com\.tr|ehesapp\.com)$/,
        intl.formatMessage({
          id: 'validations.invalidEmailForIdeconAndEhesapp',
          defaultMessage: 'Idecon ya da Ehesapp e-postası girilmelidir',
        })
      ),
    password: yup
      .string()
      .required(intl.formatMessage({ id: 'general.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<LoginFormParams>({
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema),
    shouldFocusError: true,
  });

  // async helper functions
  const login = async () => {
    if (!isValidOtpCode(code)) {
      setCodeError(
        intl.formatMessage({
          id: 'login.invalid2FACode',
          defaultMessage: 'Lütfen rakamlardan oluşan 6 haneli kodu giriniz!',
        })
      );
      codeInputRef.current?.focus();
      return;
    }

    setCodeError('');

    const postData = {
      email,
      password,
      code,
    };

    try {
      setLoading(true);

      const response = await apiService.signOn(postData);

      if (response.isSuccess) {
        LocalStorage.setItem(STORAGE_KEY_ENUM.LOGGED_USER, response.data.nameSurname);
        Authentication.setToken(response.data.token);

        setTimeout(() => {
          window.location.href = '/';
        }, 500);
      }
    } catch (err: any) {
      if (err?.response?.data?.Message === 'Doğrulama kodu hatalı!') {
        setCode('');
        setCodeError(
          intl.formatMessage({
            id: 'login.wrong2FACode',
            defaultMessage: 'Doğrulama kodu hatalı. Lütfen tekrar deneyin.',
          })
        );
        codeInputRef.current?.focus();
        return;
      }

      handleReset();
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: LoginFormParams) => {
    try {
      setLoading(true);

      const response = await apiService.sendCode(data);

      if (response.isSuccess) {
        setShowTwoFactorAuth(true);
        setEmail(data.email);
        setPassword(data.password);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  // sync helper functions
  const goToForgotPasswordPage = useCallback(() => navigate(PUBLIC_ROUTE_REGISTRY.forgotPassword.path), [navigate]);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleCodeChange = (value: string) => {
    setCode(value);
    if (codeError) setCodeError('');
  };

  const handleCodeSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void login();
  };

  const handleReset = () => {
    setShowTwoFactorAuth(false);
    setCode('');
    setCodeError('');
    reset();
  };

  return (
    <Grid container component="main" sx={{ height: '100vh' }}>
      <Grid
        size={{ xs: false, sm: 4, md: 7 }}
        sx={{
          backgroundImage:
            'url(https://images.unsplash.com/photo-1586892478025-2b5472316f22?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D)',
          backgroundRepeat: 'no-repeat',
          backgroundColor: (theme) =>
            theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.grey[900],
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <Grid size={{ xs: 12, sm: 8, md: 5 }} component={Paper} elevation={2} square>
        <Paper elevation={0}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '10px 20px',
            }}>
            <img style={{ cursor: 'pointer' }} onClick={onClickLogoHandler} width="100px" src={logoSrc} alt="logo" />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 2,
              }}>
              <LanguagePicker />
              <ColorModeIconDropdown />
              <SnowSettingsButton />
            </Box>
          </Box>
        </Paper>
        <Divider />
        <Box
          sx={{
            my: showTwoFactorAuth ? 4 : 8,
            mx: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}>
          {showTwoFactorAuth ? (
            <Box component="form" onSubmit={handleCodeSubmit} noValidate sx={{ width: '100%' }}>
              <Grid size={12} display="flex" justifyContent="center">
                <img width="100px" src={emailImgSrc} alt="" />
              </Grid>
              <Grid size={12} display="flex" justifyContent="center" mt={4}>
                <Typography component="h1" variant="h4">
                  <FormattedMessage id="login.2FA" defaultMessage="İki Adımlı Doğrulama" />
                </Typography>
              </Grid>
              <Grid size={12} display="flex" justifyContent="center">
                <Typography sx={{ mt: 1, fontSize: 14 }}>
                  <FormattedMessage
                    id="login.openAppMessage"
                    defaultMessage="Lütfen e-posta adresinize gönderilen altı haneli kodu giriniz."
                  />
                </Typography>
              </Grid>
              <Grid size={12}>
                <Box
                  sx={{
                    marginTop: '10%',
                    marginBottom: '10%',
                  }}>
                  <OtpCodeField
                    autoFocus
                    error={Boolean(codeError)}
                    helperText={codeError || undefined}
                    inputRef={codeInputRef}
                    label={<FormattedMessage id="login.otpCode" defaultMessage="Doğrulama Kodu" />}
                    name="login-verification-code"
                    onValueChange={handleCodeChange}
                    value={code}
                  />
                </Box>
              </Grid>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: '100%' }}>
                <Button
                  type="button"
                  onClick={handleReset}
                  disabled={loading}
                  variant="outlined"
                  color="error"
                  sx={{ width: { xs: '100%', sm: 'auto' }, flexShrink: 0 }}>
                  <FormattedMessage id="dashboard.cancel" defaultMessage="Vazgeç" />
                </Button>
                <Button type="submit" fullWidth variant="contained" disabled={loading} sx={{ minWidth: 0, flex: 1 }}>
                  <FormattedMessage id="login.logIn" defaultMessage="Giriş Yap" />
                </Button>
              </Stack>
            </Box>
          ) : (
            <>
              <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
                <LockOutlinedIcon />
              </Avatar>
              <Typography sx={{ fontWeight: 'bold' }} component="h1" variant="h5">
                <FormattedMessage id="login.welcome" defaultMessage="Merhaba, Hoşgeldin!" />
              </Typography>
              <Typography component="h5">
                <FormattedMessage
                  id="login.enterInformation"
                  defaultMessage="Devam etmek için lütfen kimlik bilgilerinizi girin"
                />
              </Typography>
              <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)} sx={{ mt: 1 }}>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={<FormattedMessage id="login.email" defaultMessage="E-Posta" />}
                      margin="normal"
                      fullWidth
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      autoFocus
                      autoComplete="username"
                    />
                  )}
                />
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type={showPassword ? 'text' : 'password'}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                sx={{ border: 'none' }}
                                size="small"
                                aria-label="toggle password visibility"
                                onClick={handleClickShowPassword}
                                onMouseDown={handleMouseDownPassword}
                                edge="end">
                                {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        },
                      }}
                      label={<FormattedMessage id="login.password" defaultMessage="Parola" />}
                      margin="normal"
                      fullWidth
                      error={!!errors.password}
                      helperText={errors.password?.message}
                      autoComplete="current-password"
                    />
                  )}
                />
                <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }}>
                  <FormattedMessage id="login.logIn" defaultMessage="Giriş Yap" />
                </Button>
                <Grid container spacing={2}>
                  <Grid size={12} display="flex" justifyContent="center">
                    <MuiLink
                      component="button"
                      type="button"
                      onClick={goToForgotPasswordPage}
                      variant="body2"
                      sx={{ alignSelf: 'center' }}>
                      <FormattedMessage id="login.forgotPassword" defaultMessage="Şifreni mi Unuttun?" />
                    </MuiLink>
                  </Grid>
                  <Grid size={12}>
                    <Divider>
                      <FormattedMessage id="login.or" defaultMessage="veya" />
                    </Divider>
                  </Grid>
                  <Grid size={12} display="flex" justifyContent="center">
                    <Link to={PUBLIC_ROUTE_REGISTRY.signUp.path} style={{ textDecoration: 'none', color: 'inherit' }}>
                      <FormattedMessage id="login.signUp" defaultMessage="Hesabınız yok mu? Kaydolun" />
                    </Link>
                  </Grid>
                </Grid>
                <Copyright />
              </Box>
            </>
          )}
        </Box>
      </Grid>
      <LoadingBackdrop open={loading} />
    </Grid>
  );
};

export default Login;
